import type { ScreenContext } from "@/types";
import { boundScreenContext } from "./screenContextBudget";

const MAX_RECORDS = 24;
const MAX_FORM_FIELDS = 40;
const MAX_CHART_SUMMARIES = 20;
const MAX_TEXT = 500;
const focusedControls = new WeakMap<HTMLElement, Element>();

export function rememberContextFocus(root: HTMLElement, target: Element) {
  if (
    target.matches(
      'input, textarea, select, [role="combobox"], [role="checkbox"], [role="radio"], [role="switch"]',
    )
  ) {
    focusedControls.set(root, target);
  }
}

export interface CollectedScreenContext {
  screenContext: ScreenContext;
  filters: Record<string, string>;
  hasExplicitView?: boolean;
}

function cleanText(value: string | null | undefined, max = MAX_TEXT): string {
  return (value ?? "").replace(/\s+/g, " ").trim().slice(0, max);
}

function elementText(element: Element, max = MAX_TEXT, omit?: string): string {
  const parts: string[] = [];
  const visit = (node: Node) => {
    if (node instanceof Element) {
      if (isExcluded(node) || node.matches(omit ?? "script, style")) return;
    }
    if (node.nodeType === Node.TEXT_NODE) parts.push(node.textContent ?? "");
    else node.childNodes.forEach(visit);
  };
  visit(element);
  return cleanText(parts.join(" "), max);
}

function isExcluded(element: Element): boolean {
  if (
    element.closest(
      '[hidden], [aria-hidden="true"], [data-ai-context-ignore], [role="dialog"]',
    )
  ) {
    return true;
  }
  if (typeof window === "undefined") return false;
  for (
    let current: Element | null = element;
    current;
    current = current.parentElement
  ) {
    const style = window.getComputedStyle(current);
    if (
      style.display === "none" ||
      style.visibility === "hidden" ||
      style.visibility === "collapse"
    )
      return true;
  }
  return false;
}

function fieldLabel(control: Element): string {
  const explicit = control
    .closest("[data-ai-field-label]")
    ?.getAttribute("data-ai-field-label");
  if (explicit) return cleanText(explicit, 120);
  const cell = control.closest<HTMLTableCellElement>("td");
  const row = cell?.parentElement;
  const table = cell?.closest("table");
  if (cell && row && table) {
    const header = table.querySelectorAll("thead th")[cell.cellIndex];
    const rowIndex =
      Array.from(table.querySelectorAll("tbody tr")).indexOf(row) + 1;
    if (header)
      return cleanText(`${elementText(header, 90)}, row ${rowIndex}`, 120);
  }
  const html = control as HTMLElement;
  const id = html.id;
  const associated = id
    ? Array.from(document.querySelectorAll("label[for]")).find(
        (label) => label.getAttribute("for") === id,
      )
    : null;
  const labelledBy = html.getAttribute("aria-labelledby");
  const labelledText = labelledBy
    ? labelledBy
        .split(/\s+/)
        .map((labelId) => document.getElementById(labelId))
        .filter((label): label is HTMLElement => label instanceof HTMLElement)
        .map((label) => elementText(label, 120))
        .join(" ")
    : "";
  const controlLabel =
    associated && elementText(associated, 120)
      ? elementText(associated, 120)
      : labelledText ||
        html.getAttribute("aria-label") ||
        html.closest(".form-field")?.querySelector("label")?.textContent ||
        html.getAttribute("placeholder") ||
        html.getAttribute("title") ||
        "";
  if (controlLabel) return cleanText(controlLabel, 120);

  const formControl = html.closest(
    ".MuiFormControl-root, .MuiTextField-root, .form-field, .field, [data-field-label]",
  );
  return cleanText(
    formControl?.getAttribute("data-field-label") ||
      formControl?.querySelector("label")?.textContent,
    120,
  );
}

function isSensitiveLabel(label: string, control: Element): boolean {
  const inputType = (control as HTMLInputElement).type?.toLowerCase() ?? "";
  return (
    [
      "password",
      "file",
      "hidden",
      "button",
      "submit",
      "reset",
      "image",
    ].includes(inputType) ||
    /password|secret|token|api[\s_-]*key|email|phone|mobile|address|tax[\s_-]*(id|number)|account[\s_-]*(number|id)|credit[\s_-]*card|cvv|security[\s_-]*code|routing[\s_-]*number|\biban\b|\bswift\b|\bssn\b|national[\s_-]*id/i.test(
      `${label} ${(control as HTMLElement).getAttribute("name") ?? ""}`,
    )
  );
}

function controlValue(control: Element, max = 240): string {
  const role = control.getAttribute("role");
  if (["checkbox", "radio", "switch"].includes(role ?? "")) {
    return control.getAttribute("aria-checked") === "true" ? "Yes" : "No";
  }
  if (control instanceof HTMLSelectElement) {
    return cleanText(
      Array.from(control.selectedOptions)
        .map((option) => option.textContent ?? "")
        .join(", "),
      max,
    );
  }
  if (control instanceof HTMLInputElement) {
    if (control.type === "checkbox" || control.type === "radio") {
      return control.checked ? "Yes" : "No";
    }
    return cleanText(control.value, max);
  }
  if (control instanceof HTMLTextAreaElement)
    return cleanText(control.value, max);
  return elementText(control, max);
}

function getRowFields(row: Element, headers: string[]): Record<string, string> {
  const cells = Array.from(row.querySelectorAll("td, [role=gridcell]"));
  const fields: Record<string, string> = {};
  cells.forEach((cell, index) => {
    const value = elementText(
      cell,
      240,
      'button, [role="button"], input, select, textarea, svg, script, style',
    );
    if (value) fields[headers[index] || `Column ${index + 1}`] = value;
  });
  return fields;
}

function getTableName(table: Element, index: number): string {
  const named =
    table.getAttribute("aria-label") ||
    table.getAttribute("data-ai-table-label") ||
    table
      .closest("[data-ai-table-label]")
      ?.getAttribute("data-ai-table-label") ||
    table.querySelector("caption")?.textContent ||
    table.closest("[aria-label]")?.getAttribute("aria-label");
  if (named) return cleanText(named, 120);

  let previous = table.previousElementSibling;
  while (previous) {
    if (/^H[1-6]$/.test(previous.tagName)) {
      const heading = elementText(previous, 120);
      if (heading) return heading;
    }
    previous = previous.previousElementSibling;
  }
  return `Table ${index + 1}`;
}

function getTables(root: HTMLElement): {
  records: NonNullable<ScreenContext["visibleRecords"]>;
  truncated: boolean;
} {
  const tables = Array.from(
    root.querySelectorAll("table, [role=grid], [role=table]"),
  ).filter((table) => !isExcluded(table));
  const records: NonNullable<ScreenContext["visibleRecords"]> = [];
  let truncated = false;

  tables.forEach((table, tableIndex) => {
    if (records.length >= MAX_RECORDS) {
      truncated = true;
      return;
    }
    const headers = Array.from(
      table.querySelectorAll("thead th, [role=columnheader]"),
    ).map((header) => elementText(header, 120));
    const rows = Array.from(
      table.querySelectorAll("tbody tr, [role=row]"),
    ).filter(
      (row) =>
        !row.querySelector("th, [role=columnheader]") &&
        row.querySelector("td, [role=gridcell]") &&
        !isExcluded(row),
    );
    const selected = rows.filter(
      (row) =>
        row.getAttribute("aria-selected") === "true" ||
        row.getAttribute("data-selected") === "true" ||
        row.classList.contains("Mui-selected") ||
        !!row.querySelector('input[type="checkbox"]:checked'),
    );
    const ordinary = rows.filter((row) => !selected.includes(row));
    const chosen = [...selected, ...ordinary].slice(0, 12);
    if (rows.length > chosen.length) truncated = true;

    for (const row of chosen) {
      const fields = getRowFields(row, headers);
      if (Object.keys(fields).length === 0) continue;
      records.push({
        table: getTableName(table, tableIndex),
        fields,
        ...(selected.includes(row) ? { selected: true } : {}),
      });
      if (records.length >= MAX_RECORDS) {
        truncated = true;
        break;
      }
    }
  });

  const cards = Array.from(
    root.querySelectorAll('[data-ai-record-card="true"]'),
  ).filter((card) => !isExcluded(card));
  const selectedCards = cards.filter(
    (card) =>
      card.getAttribute("data-ai-record-selected") === "true" ||
      !!card.querySelector('input[type="checkbox"]:checked'),
  );
  const chosenCards = [
    ...selectedCards,
    ...cards.filter((card) => !selectedCards.includes(card)),
  ].slice(0, 12);
  if (cards.length > chosenCards.length) truncated = true;
  for (const card of chosenCards) {
    if (records.length >= MAX_RECORDS) {
      truncated = true;
      break;
    }
    const fields: Record<string, string> = {};
    const primary = card.querySelector("[data-ai-record-primary]");
    if (primary) fields.Name = elementText(primary, 240);
    card
      .querySelectorAll<HTMLElement>("[data-ai-record-field]")
      .forEach((field) => {
        const label = cleanText(
          field.getAttribute("data-ai-record-field"),
          120,
        );
        const value = elementText(field, 240);
        if (label && value) fields[label] = value;
      });
    if (Object.keys(fields).length > 0) {
      records.push({
        table:
          cleanText(card.getAttribute("data-ai-record-table"), 120) ||
          "Visible records",
        fields,
        ...(selectedCards.includes(card) ? { selected: true } : {}),
      });
    }
    if (records.length >= MAX_RECORDS) {
      truncated = true;
      break;
    }
  }

  if (records.length > MAX_RECORDS) {
    records.splice(MAX_RECORDS);
    truncated = true;
  }

  return { records, truncated };
}

function getFilters(root: HTMLElement): Record<string, string> {
  const filters: Record<string, string> = {};
  const controls = Array.from(
    root.querySelectorAll(
      '.table-toolbar input, .table-toolbar select, .table-toolbar [role="combobox"], [role="search"] input, [role="search"] select, input[type="search"], input[placeholder*="search" i]',
    ),
  ).filter((control) => !isExcluded(control));

  controls.forEach((control, index) => {
    const label = fieldLabel(control) || `Filter ${index + 1}`;
    if (isSensitiveLabel(label, control)) return;
    const value = controlValue(control);
    if (value) filters[label] = value;
  });
  return filters;
}

function getFormFields(
  root: HTMLElement,
  focused?: Element,
): {
  fields: NonNullable<ScreenContext["formFields"]>;
  truncated: boolean;
  count: number;
  focusedField?: string;
} {
  const controls = Array.from(
    root.querySelectorAll(
      'form input:not([type="hidden"]):not([type="file"]), form select, form textarea, form [role="combobox"], form [role="checkbox"], form [role="radio"], form [role="switch"], [data-ai-form-context] input:not([type="hidden"]):not([type="file"]), [data-ai-form-context] select, [data-ai-form-context] textarea, [data-ai-form-context] [role="combobox"], [data-ai-form-context] [role="checkbox"], [data-ai-form-context] [role="radio"], [data-ai-form-context] [role="switch"]',
    ),
  ).filter(
    (control) =>
      !isExcluded(control) &&
      !!fieldLabel(control) &&
      !isSensitiveLabel(fieldLabel(control), control),
  );
  const fields: NonNullable<ScreenContext["formFields"]> = [];
  // A focused field late in a large disclosure must not disappear behind the cap.
  const ordered = [...controls].sort(
    (a, b) =>
      Number(b === focused) - Number(a === focused) ||
      Number(b.getAttribute("aria-invalid") === "true") -
        Number(a.getAttribute("aria-invalid") === "true"),
  );
  let truncated = controls.length > MAX_FORM_FIELDS;

  for (const control of ordered.slice(0, MAX_FORM_FIELDS)) {
    const label = fieldLabel(control);
    if (!label || isSensitiveLabel(label, control)) continue;
    const options =
      control instanceof HTMLSelectElement
        ? Array.from(control.options)
            .filter((option) => !option.disabled && option.value !== "")
            .slice(0, 12)
            .map((option) => cleanText(option.textContent, 100))
        : undefined;
    const helperId = control.getAttribute("aria-describedby");
    const helper = helperId
      ? document.getElementById(helperId)
      : control
          .closest(".MuiFormControl-root")
          ?.querySelector(".MuiFormHelperText-root.Mui-error");
    const error =
      control.getAttribute("aria-invalid") === "true" && helper
        ? elementText(helper, 300)
        : undefined;
    const valueLimit = control === focused ? 500 : 240;
    const value = controlValue(control, valueLimit);
    if (
      (control instanceof HTMLInputElement ||
        control instanceof HTMLTextAreaElement) &&
      control.value.length > valueLimit
    )
      truncated = true;
    fields.push({
      label,
      control:
        control instanceof HTMLSelectElement
          ? "select"
          : control instanceof HTMLTextAreaElement
            ? "textarea"
            : control.getAttribute("role") ||
              (control as HTMLInputElement).type ||
              "text",
      required:
        (control as HTMLInputElement).required ||
        control.getAttribute("aria-required") === "true" ||
        /\*\s*$/.test(label),
      ...(value ? { value } : {}),
      ...(options?.length ? { options } : {}),
      ...(error ? { error } : {}),
    });
  }

  return {
    fields,
    truncated,
    count: controls.length,
    focusedField:
      focused && controls.includes(focused) ? fieldLabel(focused) : undefined,
  };
}

function getCardSummaries(root: HTMLElement): string[] {
  return Array.from(
    root.querySelectorAll(".MuiCard-root, [data-ai-context-summary]"),
  )
    .filter((card) => !isExcluded(card))
    .map((card) => {
      const explicit = card.getAttribute("data-ai-context-summary");
      if (explicit) return cleanText(explicit, 500);
      const title = card.querySelector(
        ".MuiCardHeader-title, h2, h3, h4, [data-ai-card-title]",
      );
      const body = card.querySelector(
        ".MuiCardContent-root, [data-ai-context-summary]",
      );
      const heading = title ? elementText(title, 120) : "";
      const content = elementText(
        body ?? card,
        400,
        'button, [role="button"], input, select, textarea, table, canvas, svg, [data-ai-chart-summary], script, style',
      );
      if (!heading && !content) return "";
      return cleanText(
        heading && content && !content.startsWith(heading)
          ? `${heading}: ${content}`
          : content || heading,
        500,
      );
    })
    .filter(Boolean)
    .slice(0, 12);
}

export function collectScreenContext(
  root: HTMLElement | null,
  routeView?: string,
  sectionId?: string,
): CollectedScreenContext {
  if (!root) {
    return {
      screenContext: { elements: [], activeView: routeView },
      filters: {},
    };
  }

  const sections = Array.from(
    root.querySelectorAll<HTMLElement>("[data-ai-form-section]"),
  ).filter((section) => !isExcluded(section));
  const remembered = focusedControls.get(root);
  const focused = root.contains(document.activeElement)
    ? document.activeElement!
    : remembered && root.contains(remembered)
      ? remembered
      : undefined;
  const focusedSection = focused?.closest<HTMLElement>(
    "[data-ai-form-section]",
  );
  const selectedSection =
    sections.find(
      (section) => section.getAttribute("data-ai-form-section") === sectionId,
    ) ??
    (focusedSection &&
    sections.includes(focusedSection) &&
    focusedSection.getAttribute("data-ai-form-section-open") !== "false"
      ? focusedSection
      : undefined) ??
    sections.find(
      (section) => section.getAttribute("data-ai-form-section-open") === "true",
    );
  const formSections = sections.slice(0, 80).map((section) => ({
    id: cleanText(section.getAttribute("data-ai-form-section"), 120),
    label: cleanText(section.getAttribute("data-ai-form-section-label"), 200),
    open: section.getAttribute("data-ai-form-section-open") !== "false",
  }));

  const heading = root.querySelector("h1");
  const pageTitle = heading ? elementText(heading, 200) : undefined;
  const activeTab = root.querySelector(
    '[role="tab"][aria-selected="true"], [role="tab"][aria-current="page"]',
  );
  const explicitView = Array.from(
    root.querySelectorAll("[data-ai-active-view]"),
  ).find(
    (item) => !isExcluded(item) && item.getAttribute("data-ai-active-view"),
  );
  const selectedView = explicitView
    ? cleanText(explicitView.getAttribute("data-ai-active-view"), 100)
    : activeTab
      ? elementText(activeTab, 100)
      : routeView;
  const alerts = Array.from(
    root.querySelectorAll(
      '[role="alert"], [role="status"], [data-ai-context-summary]',
    ),
  )
    .filter((item) => !isExcluded(item))
    .map(
      (item) =>
        cleanText(item.getAttribute("data-ai-context-summary"), 500) ||
        elementText(item, 500),
    )
    .filter(Boolean)
    .slice(0, 8);
  const headingsUnbounded = Array.from(
    root.querySelectorAll("h2, h3, h4, .MuiCardHeader-title"),
  )
    .filter((item) => !isExcluded(item))
    .map((item) => elementText(item, 120))
    .filter(Boolean);
  const headings = headingsUnbounded.slice(0, 20);
  const chartsUnbounded = Array.from(
    root.querySelectorAll("[data-ai-chart-summary]"),
  )
    .filter((item) => !isExcluded(item))
    .map((item) => cleanText(item.getAttribute("data-ai-chart-summary"), 1500))
    .filter(Boolean);
  const charts = chartsUnbounded.slice(0, MAX_CHART_SUMMARIES);
  const cardSummaries = getCardSummaries(root);
  const visibleCardCount = Array.from(
    root.querySelectorAll(".MuiCard-root, [data-ai-context-summary]"),
  ).filter((card) => !isExcluded(card)).length;
  const detailRoot = selectedSection ?? root;
  const tableData = getTables(detailRoot);
  const formData = getFormFields(detailRoot, focused);
  const filters = getFilters(root);
  const keyMetrics = Object.fromEntries(
    Array.from(
      root.querySelectorAll("[data-ai-metric-label][data-ai-metric-value]"),
    )
      .filter((item) => !isExcluded(item))
      .map((item) => [
        cleanText(item.getAttribute("data-ai-metric-label"), 120),
        cleanText(item.getAttribute("data-ai-metric-value"), 500),
      ]),
  );
  const summaryParts = [
    sections.length
      ? `Form scope: ${selectedSection?.getAttribute("data-ai-form-section-label") ?? "No open section"}. Only fields in this section are shared; other sections are an outline only. Open or choose another section to inspect it. Narrative values are excerpts, not complete answers.`
      : "",
    pageTitle ? `Page: ${pageTitle}` : "",
    selectedView ? `Current view: ${selectedView}` : "",
    headings.length ? `Visible sections: ${headings.join(", ")}` : "",
    cardSummaries.length
      ? `Visible card content: ${cardSummaries.join("; ")}`
      : "",
    ...alerts,
  ].filter(Boolean);
  const context: ScreenContext = {
    elements: [],
    ...(formSections.length
      ? {
          formSections,
          activeFormSection:
            selectedSection?.getAttribute("data-ai-form-section") ?? undefined,
        }
      : {}),
    ...(formData.count
      ? { formFieldCount: formData.count, focusedField: formData.focusedField }
      : {}),
    ...(Object.keys(keyMetrics).length ? { keyMetrics } : {}),
    ...(selectedView ? { activeView: selectedView } : {}),
    ...(pageTitle ? { pageTitle } : {}),
    ...(summaryParts.length ? { summary: summaryParts.join("; ") } : {}),
    ...(tableData.records.length ? { visibleRecords: tableData.records } : {}),
    ...(charts.length ? { chartSummaries: charts } : {}),
    ...(formData.fields.length ? { formFields: formData.fields } : {}),
    ...(tableData.truncated ||
    sections.length > 80 ||
    Array.from(
      root.querySelectorAll('[data-ai-context-truncated="true"]'),
    ).some((item) => !isExcluded(item)) ||
    formData.truncated ||
    headingsUnbounded.length > headings.length ||
    chartsUnbounded.length > charts.length ||
    visibleCardCount > cardSummaries.length ||
    root.querySelectorAll('[role="alert"], [role="status"]').length >
      alerts.length
      ? { truncated: true }
      : {}),
  };

  return {
    screenContext: boundScreenContext(context),
    filters,
    hasExplicitView: !!explicitView,
  };
}
